<template>
  <v-row class="py-1 px-5 grey lighten-4 fill-height">
    <v-col cols="12">
      <v-row>
        <v-col cols="12" PagingC>
          <Paging
            :resourceSettings="resourceSettings"
            :resource="resource"
            @loadResource="loadResource"
            @pageByNumber="pageByNumber"
          />
        </v-col>

        <v-col cols="12" FILTERS>
          <v-card class="px-3" :loading="loading">
            <v-card-text>
              <v-row class="pa-0">
                <v-col cols="12" md="4" :class="`${arabic ? 'arFont' : 'enFont'} pa-1`">
                  <DateField
                    label="Create date"
                    :modelValue="filters.created_at"
                    icon="calendar_today"
                    @update:modelValue="(value) => filters.created_at = value"
                  />
                </v-col>
                <v-col cols="12" md="4" :class="`${arabic ? 'arFont' : 'enFont'} pa-1`">
                  <DateField
                    label="Appointment date"
                    :modelValue="filters.appointment"
                    icon="calendar_today"
                    @update:modelValue="(value) => filters.appointment = value"
                  />
                </v-col>
                <v-col cols="12" md="4" :class="`${arabic ? 'arFont' : 'enFont'} pa-1`">
                  <v-autocomplete
                    :items="candidates"
                    :label="i18n.candidate"
                    :class="`${arabic ? 'arFont' : 'enFont'}`"
                    v-model="filters.candidate_id"
                    outlined
                    hide-details
                    dense
                    clearable
                    item-text="name"
                    item-value="id" />
                </v-col>
                <v-col cols="12" md="4" :class="`${arabic ? 'arFont' : 'enFont'} pa-1`">
                  <v-text-field 
                    label="Result"
                    outlined
                    dense
                    hide-details
                    v-model="filters.result"
                  />
                </v-col>
                <v-col cols="12" md="4" :class="`${arabic ? 'arFont' : 'enFont'} pa-1`">
                  <v-select 
                    label="Attended"
                    dense
                    outlined
                    hide-details
                    :items="attended_items"
                    v-model="filters.attended"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="mx-auto" cols="2" style="max-width: fit-content;">
          <v-btn @click.prevent="() => (dialog = true)" color="primary">
            <v-icon>add</v-icon>
            <span>New</span>
          </v-btn>
        </v-col>
        <v-col cols="12" updates>
          <v-card class="px-3" :loading="loading">
            <v-card-text>
              <v-timeline dense clipped>
                <v-timeline-item
                  v-for="(evaluation, key) in resource.data"
                  :key="`evaluation-${key}`"
                  :color="evaluation.attended ? 'success' : 'red'"
                  small
                >
                  <v-row class="elevation-1 pa-4 py-2">
                    <v-col>
                      <v-row>
                        <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
                          <div style="font-size:11px;font-weight:700;">{{ i18n.candidate }}</div>
                          <div class="d-flex align-center">
                            <v-chip label small color="light-blue darken-1" dark class="mr-2">
                              {{ evaluation.candidate.aramcco_code }}
                            </v-chip>
                            <v-chip label small color="light-blue darken-1" dark>
                              {{ evaluation.candidate.name }}
                            </v-chip>
                          </div>
                        </v-col>
                        <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
                          <div style="font-size:11px;font-weight:700;">{{ i18n.evaluation_appointment }}</div>
                          <div class="body-1">
                            <v-chip color="blue darken-1" dark small class="mr-2">
                              {{ formatDateToCurrentLocale(evaluation.appointment.appointment)[0] }}
                            </v-chip>
                            <v-chip color="blue darken-1" dark class="mr-2" small>
                              {{ formatDateToCurrentLocale(evaluation.appointment.appointment)[1] }}
                            </v-chip>
                          </div>
                        </v-col>
                        <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
                          <div style="font-size:11px;font-weight:700;">{{ i18n.created_at }}</div>
                          <div class="body-1">
                            {{ formatDateToCurrentLocale(evaluation.created_at)[0] }}
                          </div>
                        </v-col>
                        <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
                          <div style="font-size:11px;font-weight:700;">{{ i18n.evaluation_update_date }}</div>
                          <div class="body-1">{{ getFormat(evaluation.updated_at) }}</div>
                        </v-col>
                        <v-col cols="12" md="3" class="pa-0 mb-md-0 mb-3">
                          <div style="font-size:11px;font-weight:700;">{{ i18n.was_present }}</div>
                          <div class="body-1">{{ evaluation.attended ? i18n.yes : i18n.no }}</div>
                        </v-col>
                        <v-col cols="12" md="9" class="pa-0 mt-2" v-if="evaluation.note">
                          <div style="font-size:11px;font-weight:700;">{{ i18n.evaluation_note }}</div>
                          <div class="body-1">
                            {{ evaluation.note }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col v-if="evaluation.result" md="6" col="12" class="pa-0">
                          <div class="mb-1">
                            <div style="font-size:11px;font-weight:700;">{{ i18n.evaluation_result }}</div>
                          </div>
                          <v-chip label small color="blue" dark>
                            {{ evaluation.result }}
                          </v-chip>
                        </v-col>
                      </v-row>
                      <v-row class="mt-2">
                        <v-col col="12" class="pa-0">
                          <v-btn color="primary" small class="mr-2" @click.prevent="editEvaluation(evaluation)">
                            <v-icon x-small class="mr-1">edit</v-icon>
                            Edit
                          </v-btn>
                          <v-btn color="error" small class="mr-2" @click.prevent="deleteEvaluation(evaluation)">
                            <v-icon x-small class="mr-1">delete</v-icon>
                            Delete
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-timeline-item>
              </v-timeline>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
    <multiDialog
      :dialog="dialog"
      :loading="loading"
      :resourceSettings="resourceSettings"
      :deleting="deleting"
      :resourceKeys="resourceKeys"
      :newResource="newResource"
      @closeDialog="
        () => {
          dialog = false;
          newResource = {};
        }
      "
      @updateResource="updateResource"
      @postResource="postResource"
      @deleteResource="deleteResource"
    />
  </v-row>
</template>

<script>
import Paging from "@/components/Paging";
import multiDialog from "@/components/multiDialog";
import { format } from "timeago.js";
import DateField from "../components/partials/DateField.vue";
import _ from 'lodash'

export default {
  components: {
    Paging,
    multiDialog,
    DateField
},
  data() {
    return {
      dialog: false,
      loading: false,
      loading: false,
      deleting: false,
      resourceKeys: [
        { name: "appointment", label: "appointment", type: "date", required: true, readonly: false },
        { name: "appointment_time", label: "appointment_time", type: "time", required: true, readonly: false },
        {
          name: "candidate_id",
          label: "candidate",
          type: "select",
          items: "candidates",
          data: [],
          source: "db",
          required: true,
          readonly: false,
        },
        { name: "attended", label: "attended", type: "switch", required: false, readonly: false },
        { name: "result", label: "result", type: "text", required: false, readonly: false },
        { name: "note", label: "note", type: "textarea", required: false, readonly: false },
      ],
      newResource: {},
      page: 1,
      loading: false,
      resource: [],
      filters: {
        created_at: null,
        appointment: null,
        candidate_id: null,
        result: null,
        attended: null,
      },
      attended_items: [
        {text: 'Any', value: null},
        {text: 'Yes', value: true},
        {text: 'No', value: false},
      ],
      candidates: [],
      resourceSettings: {
        resourceName: "evaluations",
        create: true,
        pagination: true,
      },
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    language() {
      return this.$store.state.language;
    },
    i18n() {
      return this.$store.state.i18n[this.$store.state.language];
    },
    arabic() {
      if (this.$store.state.language == "ar") return true;
    },
  },
  watch: {
    filters: {
      handler: _.debounce(function () {
        this.loadResource()
      }, 500), 
      deep: true
    }
  },
  methods: {
    formatDateToCurrentLocale(date) {
      let new_date = new Date(date);
      return [
        new_date.toLocaleDateString(this.arabic ? "ar" : "en", {
          day: "numeric",
          year: "numeric",
          month: "short",
        }),
        new_date.toLocaleTimeString(this.arabic ? "ar" : "en"),
      ];
    },
    deleteResource(r) {
      return new Promise(async (res, rej)=>{
        this.loading = true
        await this.$store.dispatch('deleteResource', {resource:`/can/delete_/evaluations/${r.id}`})
        this.dialog = false
        this.deleting = false
        this.loading = false
        await this.loadResource()
        this.newResource = {}
        res(true)
      })
    },
    deleteEvaluation(e) {
      this.newResource = e;
      this.deleting = true;
      this.dialog = true;
    },
    editEvaluation(e) {
      this.newResource = {
        ...e,
        appointment: e.appointment.appointment.split(" ")[0],
        appointment_time: e.appointment.appointment.split(" ")[1],
      };
      this.dialog = true;
    },
    getFormat(d) {
      return format(d);
    },
    buildFilter() {
      let filter = ``
      Object.keys(this.filters).map(key => {
        let value = this.filters[key]
        if (value != null) {
          filter += `&filter[${key}]=${value}`
        }
      })
      return filter
    },
    updateResource(r) {
      return new Promise(async (res, rej) => {
        r.appointment = `${r.appointment} ${r.appointment_time}`;
        this.loading = true;

        await this.$store.dispatch("putResource", {
          resource: `/can/update_/evaluations/${r.id}`,
          value: r,
        });

        this.dialog = false;
        this.loading = false;
        this.newResource = {};

        await this.loadResource();

        res(true);
      });
    },
    postResource() {
      return new Promise(async (res, rej) => {
        this.newResource.appointment = this.newResource.appointment + " " + this.newResource.appointment_time;
        delete this.newResource.appointment_time;

        this.loading = true;

        await this.$store.dispatch("postResource", {
          resource: "/can/create_/evaluations",
          value: this.newResource,
        });

        this.dialog = false;
        this.loading = false;
        this.newResource = {};

        await this.loadResource();

        res(true);
      });
    },
    loadResource() {
      return new Promise(async (res, rej) => {
        this.loading = true;
        this.resource = await this.$store.dispatch("getResource", {
          resource: "/can/get_all_/evaluations",
          page: `?page=${this.page}` + this.buildFilter(),
        });
        this.loading = false;

        await this.loadItems();

        res(true);
      });
    },
    pageByNumber(n) {
      return new Promise(async (res, rej) => {
        this.loading = true;
        this.page = n;
        this.resource = await this.$store.dispatch("getResource", {
          resource: "/can/get_all_/evaluations",
          page: `?page=${n}` + this.buildFilter(),
        });
        this.loading = false;
        res(true);
      });
    },
    async loadItems() {
      return new Promise(async (res, rej) => {
        let iks = ["candidates"];

        for (let i = 0; i < iks.length; i++) {
          const f = iks[i];
          let t = this.resource[f];
          if (!t.error) {
            t.forEach((x) => (this.$store.state.i18n.en[x.slug] = x.en));
            t.forEach((x) => (this.$store.state.i18n.ar[x.slug] = x.ar));
            t.forEach((x) => (this.$store.state.i18n.fr[x.slug] = x.fr));
            this[f] = t;
          }
        }

        let ks = this.resourceKeys.filter((x) => x.type == "select" && x.source == "db");

        (async (x) => {
          for (let i = 0; i < ks.length; i++) {
            const f = ks[i];
            console.log(f.items);
            f.data = this.resource[f.items].map((x) => {
              return {
                ...x,
                text: x.name ? x.name : x.en ? x.en : "unknown",
                [`${f.name}_id`]: x.id,
              };
            });
          }
          res(true);
        })();
      });
    },
  },
  async created() {
    await this.loadResource();
  },
};
</script>
